import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSendYoutubeRequest } from "../services/stagemode_api/viewers/viewers_api";
import { useAuth } from "../hooks";

export interface YouTubeRequestSectionProps {
  channel: string;
  bookingId?: string | null;
  participantId?: string | null;
  displayName?: string | null;
}

const isValidYouTubeUrl = (url: string) => {
  try {
    const u = new URL(url);
    if (
      !["www.youtube.com", "youtube.com", "youtu.be", "m.youtube.com"].includes(
        u.hostname
      )
    )
      return false;
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.length > 1; // /VIDEO_ID
    }
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      return Boolean(v && v.length >= 5);
    }
    return false;
  } catch {
    return false;
  }
};

const YouTubeRequestSection: React.FC<YouTubeRequestSectionProps> = ({
  channel,
  bookingId,
  participantId,
  displayName,
}) => {
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [songNameWithArtist, setSongNameWithArtist] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    msg: "",
    severity: "success",
  });
  const { userInfo } = useAuth();

  const { update: sendYoutubeRequest } = useSendYoutubeRequest({
    key: ["send-youtube-request"],
    params: {
      id: bookingId ?? "",
      userId: userInfo?.id ?? "",
    },
  });

  const handleSubmit = async () => {
    const trimmed = url.trim();
    const songTrimmed = songNameWithArtist.trim();

    if (!trimmed && !songTrimmed) {
      setToast({
        open: true,
        msg: "Please provide a YouTube URL or a song name with artist.",
        severity: "error",
      });
      return;
    }

    if (trimmed && !isValidYouTubeUrl(trimmed)) {
      setToast({
        open: true,
        msg: "Please paste a valid YouTube URL.",
        severity: "error",
      });
      return;
    }
    setSubmitting(true);
    try {
      await sendYoutubeRequest({
        youtube_url: trimmed || undefined,
        message: note.trim() || undefined,
        song_name_with_artist: songTrimmed || undefined,
      });
      setToast({
        open: true,
        msg: "Request sent to the DJ/moderator!",
        severity: "success",
      });
      setUrl("");
      setNote("");
      setSongNameWithArtist("");
    } catch (e: any) {
      setToast({
        open: true,
        msg: e?.message || "Could not send request",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        background: "linear-gradient(135deg, #0f0f0f 0%, #171717 100%)",
        color: "#fff",
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontFamily: "Lexend", fontWeight: 700, mb: 1 }}
      >
        🎵 Song / YouTube Request
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.75)", mb: 2 }}
      >
        Paste a YouTube link or type a song request.
      </Typography>

      <Stack spacing={1.5}>
        <TextField
          fullWidth
          value={songNameWithArtist}
          onChange={(e) => setSongNameWithArtist(e.target.value)}
          placeholder="Song name with artist (optional) e.g. Hello - Adele"
          InputProps={{ sx: { color: "#fff", fontFamily: "Lexend" } }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <TextField
          fullWidth
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          InputProps={{ sx: { color: "#fff", fontFamily: "Lexend" } }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <TextField
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note to DJ (e.g., dedication)"
          InputProps={{ sx: { color: "#fff", fontFamily: "Lexend" } }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.2)",
            },
          }}
        />
        <Box>
          <Button
            onClick={handleSubmit}
          disabled={submitting || (!url.trim() && !songNameWithArtist.trim())}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend",
              fontWeight: 600,
              backgroundColor: "#E50914",
              "&:hover": { backgroundColor: "#c10812" },
            }}
          >
            Send Request
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default YouTubeRequestSection;
